/**
 * The header bell (FR-7, AC-13, AC-17).
 *
 * Answers one question at a glance — "is anything waiting for me?" — and gives
 * the two actions that follow from the answer: read the newest few here, or
 * open the full notification centre.
 *
 * It polls `/api/notifications/summary` on the same five-second cadence as the
 * dashboard, so a colleague submitting an expense lights the badge without a
 * refresh. The interval comes back in the payload rather than being hard-coded
 * on both sides; the constant below is only the fallback used before the first
 * response lands.
 *
 * The shared notification view types live here, next to the smallest thing that
 * renders one, and the notification centre imports them.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { type ApiRequestError, apiGet, apiPatch, apiPost } from '../lib/api-client';
import { useAuth } from '../lib/auth';
import { queryKeys } from '../queries/keys';
import { errorMessage } from '../screens/accounts/AccountForm';

/** Used only until the first summary response reports the server's cadence. */
export const DEFAULT_POLL_INTERVAL_MS = 5_000;

export type NotificationSeverity = 'info' | 'warning' | 'critical';

export interface NotificationView {
  id: string;
  type: string;
  typeLabel: string;
  severity: NotificationSeverity;
  severityLabel: string;
  title: string;
  body: string;
  entityType: string | null;
  entityId: string | null;
  linkPath: string | null;
  read: boolean;
  readAt: string | null;
  createdAt: string;
  /** Server-worded relative age, e.g. "3 hours ago". */
  ageLabel: string;
}

export interface NotificationSummaryResponse {
  unreadCount: number;
  total: number;
  latest: NotificationView[];
  pollIntervalMs: number;
}

export interface NotificationListResponse {
  data: NotificationView[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
  unreadCount: number;
  counts: { all: number; unread: number; read: number };
  types: { value: string; label: string; total: number; unread: number }[];
}

export interface MarkAllReadResponse {
  updated: number;
  unreadCount: number;
}

/** Tone classes per severity, so the centre and the bell agree on colour. */
export const SEVERITY_TONE: Record<NotificationSeverity, string> = {
  info: 'bg-state-neutral-soft text-ink-900',
  warning: 'bg-state-caution-soft text-ink-900',
  critical: 'bg-state-negative-soft text-state-negative',
};

export const SEVERITY_DOT: Record<NotificationSeverity, string> = {
  info: 'bg-state-neutral',
  warning: 'bg-state-caution',
  critical: 'bg-state-negative',
};

/** ISO timestamp → a readable local date and time, or an em dash. */
export function formatNotificationTime(value: string | null): string {
  if (value === null) {
    return '—';
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime())
    ? value
    : parsed.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

export function unreadBadgeLabel(count: number): string {
  return count > 99 ? '99+' : String(count);
}

function BellIcon({ className }: { className?: string }): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={className}
    >
      <path d="M18 8a6 6 0 1 0-12 0c0 5-2 6-2 6h16s-2-1-2-6" />
      <path d="M10.3 20a2 2 0 0 0 3.4 0" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Confirmation
// ---------------------------------------------------------------------------

export interface ConfirmDialogProps {
  title: string;
  description: string;
  confirmLabel: string;
  isPending: boolean;
  serverError: unknown;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Small confirmation used by both surfaces before clearing everything. Marking
 * every notification read is one click that erases a queue of work markers, so
 * it says how many and asks first.
 */
export function ConfirmDialog({
  title,
  description,
  confirmLabel,
  isPending,
  serverError,
  onConfirm,
  onCancel,
}: ConfirmDialogProps): JSX.Element {
  const titleId = useId();
  const confirmRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    confirmRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape' && !isPending) {
        onCancel();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [isPending, onCancel]);

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center overflow-y-auto bg-surface-inverse/60 p-4 sm:items-center">
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="tvm-card w-full max-w-md space-y-4 p-5 shadow-raised"
      >
        <h2 id={titleId} className="text-base font-semibold text-ink-900">
          {title}
        </h2>
        <p className="text-sm leading-relaxed text-ink-700">{description}</p>
        {serverError !== null && serverError !== undefined && (
          <p role="alert" className="text-sm text-state-negative">
            {errorMessage(serverError)}
          </p>
        )}
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            className="tvm-button-secondary"
            onClick={onCancel}
            disabled={isPending}
          >
            Cancel
          </button>
          <button
            ref={confirmRef}
            type="button"
            className="tvm-button-primary"
            onClick={onConfirm}
            disabled={isPending}
          >
            {isPending ? 'Working…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Bell
// ---------------------------------------------------------------------------

export function NotificationBell(): JSX.Element | null {
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [isOpen, setOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const panelId = useId();

  const enabled = auth.isAuthenticated;

  const summaryQuery = useQuery<NotificationSummaryResponse, ApiRequestError>({
    queryKey: queryKeys.notifications.summary(),
    queryFn: () => apiGet<NotificationSummaryResponse>('/api/notifications/summary'),
    enabled,
    retry: false,
    // The five-second dashboard poll the badge rides on. `refetchInterval`
    // pauses itself while the tab is hidden, so an idle browser is quiet.
    refetchInterval: DEFAULT_POLL_INTERVAL_MS,
    refetchOnWindowFocus: true,
  });

  const invalidate = useCallback((): void => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.notifications.all() });
  }, [queryClient]);

  const markRead = useMutation<NotificationView, ApiRequestError, string>({
    mutationFn: (id: string) =>
      apiPatch<NotificationView>(`/api/notifications/${id}`, { read: true }),
    onSuccess: invalidate,
  });

  const markAllRead = useMutation<MarkAllReadResponse, ApiRequestError, void>({
    mutationFn: () => apiPost<MarkAllReadResponse>('/api/notifications/read-all', {}),
    onSuccess: () => {
      setConfirming(false);
      invalidate();
    },
  });

  // A navigation always closes the panel: tapping an item must not leave the
  // drop-down covering the screen it just opened.
  useEffect(() => {
    setOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const onPointerDown = (event: MouseEvent): void => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };
    window.addEventListener('mousedown', onPointerDown);
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('mousedown', onPointerDown);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen]);

  if (!enabled) {
    return null;
  }

  const summary = summaryQuery.data;
  const unread = summary?.unreadCount ?? 0;
  const items = summary?.latest ?? [];

  const open = (item: NotificationView): void => {
    if (!item.read) {
      markRead.mutate(item.id);
    }
    setOpen(false);
    navigate(item.linkPath ?? '/notifications');
  };

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        className="tvm-button-ghost relative min-w-touch px-2 text-ink-300 hover:bg-surface-inverse-raised hover:text-surface-raised"
        aria-label={
          unread === 0 ? 'Notifications' : `Notifications, ${unreadBadgeLabel(unread)} unread`
        }
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        aria-controls={panelId}
        onClick={() => setOpen((previous) => !previous)}
      >
        <BellIcon className="h-5 w-5" />
        {unread > 0 && (
          <span
            className="absolute -right-0.5 -top-0.5 inline-flex min-w-[1.25rem] items-center justify-center rounded-xl bg-brand-red px-1 text-[0.65rem] font-bold leading-5 text-surface-raised"
            aria-hidden="true"
          >
            {unreadBadgeLabel(unread)}
          </span>
        )}
      </button>

      {/* Announced quietly so a screen reader hears the badge change. */}
      <span aria-live="polite" className="sr-only">
        {unread === 0 ? 'No unread notifications' : `${String(unread)} unread notifications`}
      </span>

      {isOpen && (
        <div
          id={panelId}
          role="dialog"
          aria-label="Notifications"
          className="fixed inset-x-2 top-14 z-40 max-h-[70vh] overflow-y-auto rounded-lg border border-line-subtle bg-surface-raised shadow-raised sm:absolute sm:inset-x-auto sm:right-0 sm:top-full sm:mt-2 sm:w-96"
        >
          <div className="flex items-center justify-between gap-2 border-b border-line-subtle px-4 py-3">
            <h2 className="text-sm font-semibold text-ink-900">Notifications</h2>
            <button
              type="button"
              className="tvm-button-ghost px-2 text-xs"
              disabled={unread === 0 || markAllRead.isPending}
              onClick={() => setConfirming(true)}
            >
              Mark all read
            </button>
          </div>

          {summaryQuery.isPending && (
            <div className="space-y-2 p-4" aria-busy="true">
              <div className="tvm-skeleton h-4 w-3/4" />
              <div className="tvm-skeleton h-4 w-1/2" />
              <span className="sr-only">Loading notifications…</span>
            </div>
          )}

          {summaryQuery.isError && (
            <div className="space-y-2 p-4">
              <p role="alert" className="text-sm text-state-negative">
                {errorMessage(summaryQuery.error)}
              </p>
              <button
                type="button"
                className="tvm-button-secondary"
                onClick={() => void summaryQuery.refetch()}
              >
                Try again
              </button>
            </div>
          )}

          {!summaryQuery.isPending && !summaryQuery.isError && items.length === 0 && (
            <p className="p-4 text-sm text-ink-700">
              Nothing is waiting for you. Approvals, overdue reviews, and email problems show up
              here.
            </p>
          )}

          <ul className="divide-y divide-line-subtle">
            {items.map((item) => (
              <li key={item.id}>
                <button
                  type="button"
                  className={clsx(
                    'flex w-full items-start gap-3 px-4 py-3 text-left transition-colors duration-150 ease-standard hover:bg-surface-sunken',
                    !item.read && 'bg-surface-sunken/60',
                  )}
                  onClick={() => open(item)}
                >
                  <span
                    className={clsx(
                      'mt-1.5 h-2 w-2 shrink-0 rounded-xl',
                      SEVERITY_DOT[item.severity],
                    )}
                    aria-hidden="true"
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-ink-900">
                      {item.title}
                    </span>
                    <span className="mt-0.5 block text-xs text-ink-500">
                      {item.typeLabel} · {item.ageLabel}
                    </span>
                  </span>
                  {!item.read && (
                    <span className="tvm-pill bg-brand-red-soft text-brand-red-strong">New</span>
                  )}
                </button>
              </li>
            ))}
          </ul>

          <div className="border-t border-line-subtle p-3">
            <button
              type="button"
              className="tvm-button-secondary w-full"
              onClick={() => {
                setOpen(false);
                navigate('/notifications');
              }}
            >
              See all notifications
            </button>
          </div>
        </div>
      )}

      {confirming && (
        <ConfirmDialog
          title="Mark everything read?"
          description={`This clears the badge for all ${String(unread)} unread notification${
            unread === 1 ? '' : 's'
          }. Nothing is deleted — you can still find them in the notification centre.`}
          confirmLabel="Mark all read"
          isPending={markAllRead.isPending}
          serverError={markAllRead.error}
          onConfirm={() => markAllRead.mutate()}
          onCancel={() => setConfirming(false)}
        />
      )}
    </div>
  );
}
